/**
 * GridMap cell-stream decoding, pure and THREE-free. `data = { "cells":
 * PackedInt32Array(...) }` holds int32 triplets `[keyLo, keyHi, cell]`: the IndexKey
 * packs int16 x/y/z (x = keyLo low 16, y = keyLo high 16, z = keyHi low 16), and
 * the cell int packs the item id (bits 0-15) and an orientation 0-23 (bits 16-20).
 */
import { warn } from '../../../logger.js';
import { slotInt } from '../../../godot/variant/intSlots.js';
import { toInt16, toUint32 } from '../../../godot/variant/intWidth.js';
import { dictCallField } from '../../../godot/variant/variantParser.js';
/**
 * The int body of the `"cells"` entry of the `data` Dictionary, or null when it holds none.
 * `GridMap::_set` reads it as `Vector<int> cells = d["cells"]` (grid_map.cpp:66-67), and the
 * parser and the linter both read it through this one reader.
 */
export const readCellsField = dictCallField('cells', 'PackedInt32Array');
/** Elements per cell: the two IndexKey halves and the packed cell int. */
const INTS_PER_CELL = 3;
export function decodeGridMapCells(packedInt32) {
    // `slotInt` rather than `Number`: `Number('inf')` is NaN, while `inf` is a
    // literal Godot writes into an INT array, and the two must stay
    // distinguishable.
    const ints = packedInt32
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
        .map((s) => slotInt(s));
    const cells = [];
    for (let i = 0; i + 2 < ints.length; i += INTS_PER_CELL) {
        const record = ints.slice(i, i + INTS_PER_CELL);
        // Skip the whole record, not the element: `toUint32(NaN)` is 0, which
        // would draw a phantom cell at the origin. The stream is fixed-stride, so
        // this costs that cell and no other.
        if (record.some((n) => n === null)) {
            warn(`[GridMap] cell data element "${record.join(', ')}" is not a cell Godot can place`);
            continue;
        }
        const keyLo = toUint32(record[0]);
        const keyHi = toUint32(record[1]);
        const cell = toUint32(record[2]);
        cells.push({
            x: toInt16(keyLo & 0xffff),
            y: toInt16((keyLo >>> 16) & 0xffff),
            z: toInt16(keyHi & 0xffff),
            item: cell & 0xffff,
            rot: (cell >>> 16) & 0x1f,
        });
    }
    return cells;
}
/**
 * Godot's 24 orthogonal cell orientations (`grid_map.cpp` `_ortho_bases`), each
 * a row-major 3×3 rotation. A cell's `rot` indexes this table. The component
 * turns the chosen basis into a THREE.Matrix4.
 */
export const ORTHO_BASES = [
    [1, 0, 0, 0, 1, 0, 0, 0, 1],
    [0, -1, 0, 1, 0, 0, 0, 0, 1],
    [-1, 0, 0, 0, -1, 0, 0, 0, 1],
    [0, 1, 0, -1, 0, 0, 0, 0, 1],
    [1, 0, 0, 0, 0, -1, 0, 1, 0],
    [0, 0, 1, 1, 0, 0, 0, 1, 0],
    [-1, 0, 0, 0, 0, 1, 0, 1, 0],
    [0, 0, -1, -1, 0, 0, 0, 1, 0],
    [1, 0, 0, 0, -1, 0, 0, 0, -1],
    [0, 1, 0, 1, 0, 0, 0, 0, -1],
    [-1, 0, 0, 0, 1, 0, 0, 0, -1],
    [0, -1, 0, -1, 0, 0, 0, 0, -1],
    [1, 0, 0, 0, 0, 1, 0, -1, 0],
    [0, 0, -1, 1, 0, 0, 0, -1, 0],
    [-1, 0, 0, 0, 0, -1, 0, -1, 0],
    [0, 0, 1, -1, 0, 0, 0, -1, 0],
    [0, 0, 1, 0, 1, 0, -1, 0, 0],
    [0, -1, 0, 0, 0, 1, -1, 0, 0],
    [0, 0, -1, 0, -1, 0, -1, 0, 0],
    [0, 1, 0, 0, 0, -1, -1, 0, 0],
    [0, 0, 1, 0, -1, 0, 1, 0, 0],
    [0, 1, 0, 0, 0, 1, 1, 0, 0],
    [0, 0, -1, 0, 1, 0, 1, 0, 0],
    [0, -1, 0, 0, 0, -1, 1, 0, 0],
];
//# sourceMappingURL=cellData.js.map